import React from 'react'
import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'

export interface ConcentrationEntry {
  id?: number | null
  name: string
  pipelineValue: number
  pipelineSharePct: number
}

export interface ConcentrationRiskData {
  totalPipelineValue: number
  topDevelopers: ConcentrationEntry[]
  topBrokers: ConcentrationEntry[]
  topCities: ConcentrationEntry[]
  flagged: boolean
  flaggedDimensions: string[]
}

interface Props {
  data: ConcentrationRiskData | undefined
  loading?: boolean
  error?: unknown
  /** Called when a row in an interactive dimension is clicked. */
  onEntryClick?: (dimension: 'developers' | 'brokers' | 'cities', entry: ConcentrationEntry) => void
  /** Entries in these dimensions will be rendered as clickable buttons. */
  interactiveDimensions?: Array<'developers' | 'brokers' | 'cities'>
  /** Entry names currently pinned by the filter (visual highlight only). */
  activeNames?: {
    developers?: string[]
    brokers?: string[]
    cities?: string[]
  }
}

function euro(value: number): string {
  if (value >= 1_000_000_000) return `€${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${Math.round(value)}`
}

function pct(value: number): string {
  return `${value.toFixed(1)}%`
}

function DimensionColumn({
  title,
  entries,
  flagged,
  interactive,
  activeNames,
  onEntryClick,
}: {
  title: string
  entries: ConcentrationEntry[]
  flagged: boolean
  interactive?: boolean
  activeNames?: string[]
  onEntryClick?: (entry: ConcentrationEntry) => void
}) {
  const totalShare = entries.reduce((acc, e) => acc + e.pipelineSharePct, 0)
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-secondary-900">{title}</h3>
        <span
          className={`text-xs font-medium ${
            flagged ? 'text-red-600' : 'text-secondary-500'
          }`}
        >
          Top-3: {pct(totalShare)}
        </span>
      </div>
      <ul className="space-y-2">
        {entries.length === 0 && (
          <li className="text-xs text-secondary-400">No data</li>
        )}
        {entries.map((e, i) => {
          const active = !!activeNames?.includes(e.name)
          const body = (
            <>
              <div className="flex items-center justify-between">
                <span
                  className={`font-medium truncate max-w-[60%] ${
                    active ? 'text-primary-700' : 'text-secondary-800'
                  }`}
                >
                  {i + 1}. {e.name}
                </span>
                <span className="text-secondary-600">{euro(e.pipelineValue)}</span>
              </div>
              <div className="mt-1 h-1.5 bg-secondary-100 rounded">
                <div
                  className={`h-full rounded ${
                    flagged ? 'bg-red-500' : 'bg-primary-500'
                  }`}
                  style={{ width: `${Math.min(100, e.pipelineSharePct * 2.5)}%` }}
                />
              </div>
              <div className="text-xs text-secondary-500 mt-0.5">{pct(e.pipelineSharePct)}</div>
            </>
          )

          if (interactive && onEntryClick) {
            return (
              <li key={`${e.name}-${i}`} className="text-sm">
                <button
                  type="button"
                  onClick={() => onEntryClick(e)}
                  className={`w-full text-left rounded px-2 py-1 -mx-2 transition-colors ${
                    active
                      ? 'bg-primary-50 ring-1 ring-primary-200'
                      : 'hover:bg-secondary-50'
                  }`}
                  title={active ? `Click to remove ${e.name} from filter` : `Click to filter by ${e.name}`}
                >
                  {body}
                </button>
              </li>
            )
          }

          return (
            <li key={`${e.name}-${i}`} className="text-sm">
              {body}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function ConcentrationRiskPanel({
  data,
  loading,
  error,
  onEntryClick,
  interactiveDimensions,
  activeNames,
}: Props) {
  if (error) {
    return <div className="card text-sm text-red-600">Failed to load concentration risk.</div>
  }
  const isInteractive = (dim: 'developers' | 'brokers' | 'cities') =>
    !!onEntryClick && (interactiveDimensions?.includes(dim) ?? false)

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-secondary-900 flex items-center gap-2">
            Concentration risk
            {data?.flagged ? (
              <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                <AlertTriangle className="h-3 w-3" />
                Flagged
              </span>
            ) : data ? (
              <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                <ShieldCheck className="h-3 w-3" />
                Balanced
              </span>
            ) : null}
          </h2>
          <p className="text-xs text-secondary-500">
            Top-3 exposure by pipeline value{' '}
            {data ? `· total ${euro(data.totalPipelineValue)}` : ''}
            {data?.flagged
              ? ` · flagged: ${data.flaggedDimensions.join(', ')}`
              : ''}
          </p>
        </div>
        <ChartInfoButton
          title="Concentration risk"
          info={{
            definition:
              'Share of total pipeline value held by the top 3 developers, brokers and cities. Flags a dimension as risky when the top-3 share exceeds 60% of GMV.',
            formula:
              'share_i = SUM(unit.current_price by dimension_i) / SUM(unit.current_price) × 100 — computed on available + reserved units',
            calculation:
              'Dimension is "flagged" if the combined top-3 share > 60%. Cities dimension is click-to-filter today; developer and broker filters land with P2.1 once the DTO exposes those fields.',
            source: 'unit.current_price, project.developer_id, project.broker_id, project.city',
          }}
        />
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 animate-pulse bg-secondary-100 rounded" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DimensionColumn
            title="Developers"
            entries={data.topDevelopers}
            flagged={data.flaggedDimensions.includes('developers')}
            interactive={isInteractive('developers')}
            activeNames={activeNames?.developers}
            onEntryClick={
              isInteractive('developers')
                ? (e) => onEntryClick?.('developers', e)
                : undefined
            }
          />
          <DimensionColumn
            title="Brokers"
            entries={data.topBrokers}
            flagged={data.flaggedDimensions.includes('brokers')}
            interactive={isInteractive('brokers')}
            activeNames={activeNames?.brokers}
            onEntryClick={
              isInteractive('brokers')
                ? (e) => onEntryClick?.('brokers', e)
                : undefined
            }
          />
          <DimensionColumn
            title="Cities"
            entries={data.topCities}
            flagged={data.flaggedDimensions.includes('cities')}
            interactive={isInteractive('cities')}
            activeNames={activeNames?.cities}
            onEntryClick={
              isInteractive('cities')
                ? (e) => onEntryClick?.('cities', e)
                : undefined
            }
          />
        </div>
      )}
    </div>
  )
}
